"use client";

import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { useApi } from "@/hooks/useApi";
import { SessionSkeleton } from "@/components/codesmith/SessionSkeleton";
import { CodesmithDisabledNotice } from "@/components/codesmith/CodesmithDisabledNotice";
import {
  useActiveGithubOrgForUser,
  useConnectedGithubOrgs,
} from "@/hooks/useConnectedGithubOrgs";
import { addCodesmithExtensionEmbed } from "@/lib/codesmithExtensionEmbed";
import { useCodesmithSurface } from "@/lib/codesmithSurface";
import { applyCodesmithExtensionContext } from "@/lib/codesmithExtensionContext";
import { KeepAppRailOnMobile } from "@/components/sidebar/mobileRailVisibility";

export default function CodesmithPrPage() {
  const router = useRouter();
  const api = useApi();
  const [resolveError, setResolveError] = useState<string | null>(null);
  // Use the suspense variant so `codesmithEnabled` reflects loaded installation
  // data. The non-suspense variant defaults to false while orgs are still
  // loading, which would prematurely redirect codesmith-enabled orgs on a cold
  // load before installation data arrives.
  const { codesmithEnabled } = useActiveGithubOrgForUser();
  // Org names repeat across GitHub instances; the lookup needs the one this
  // page is scoped to.
  const { connectedGithubOrgs } = useConnectedGithubOrgs();
  const instanceSlug = connectedGithubOrgs?.instance_slug;

  const hasPrRouteParams = Boolean(
    router.query.org && router.query.repo && router.query.prNumber,
  );
  const surface = useCodesmithSurface(router.query.embed);
  const extensionEmbed = surface === "extension";
  // The extension and desktop app have no run history to redirect to, so a
  // disabled org renders the error in place.
  const codesmithOnlySurface = surface !== "web";
  const codesmithDisabledInEmbed =
    router.isReady &&
    hasPrRouteParams &&
    !codesmithEnabled &&
    codesmithOnlySurface;
  const error =
    router.isReady && !hasPrRouteParams
      ? "Invalid PR URL format. Expected: /{org}/codesmith/{repo}/pr/{prNumber}"
      : resolveError;

  const replaceRoute = useEffectEvent((path: string) => {
    router.replace(path);
  });

  useEffect(() => {
    if (!router.isReady) return;

    const {
      org,
      repo,
      prNumber,
      prompt,
      autoSend,
      entry,
      ref: refParam,
      autoLogin,
      migration_autofix: migrationAutofix,
    } = router.query;

    if (
      typeof org !== "string" ||
      typeof repo !== "string" ||
      typeof prNumber !== "string"
    ) {
      return;
    }

    // Orgs without codesmith access landing on a codesmith route should be
    // redirected to the run history overview instead of attempting to resolve
    // a session they cannot view.
    if (!codesmithEnabled) {
      if (!codesmithOnlySurface) replaceRoute(`/${org}/runs/jobs`);
      return;
    }

    const repository = `${org}/${repo}`;

    let cancelled = false;

    api
      .get("/assistant/sessions/lookup", {
        params: {
          org,
          instance_slug: instanceSlug,
          kind: "github_pr",
          key: `${repository}#${prNumber}`,
        },
      })
      .then((response) => {
        if (cancelled) return;

        const forwardParams = new URLSearchParams();
        if (prompt) forwardParams.set("prompt", String(prompt));
        if (autoSend) forwardParams.set("autoSend", String(autoSend));
        if (entry) forwardParams.set("entry", String(entry));
        if (refParam) forwardParams.set("ref", String(refParam));
        if (autoLogin) forwardParams.set("autoLogin", String(autoLogin));
        if (migrationAutofix) {
          forwardParams.set("migration_autofix", String(migrationAutofix));
        }

        if (response.data.session_id) {
          // PR already has a session, redirect there
          const basePath = `/${org}/sessions/${response.data.session_id}`;
          const qs = forwardParams.toString();
          const path = qs ? `${basePath}?${qs}` : basePath;
          replaceRoute(
            extensionEmbed ? addCodesmithExtensionEmbed(path) : path,
          );
        } else {
          // No session yet. Pass the PR metadata from the GitHub API
          // response so the new-session view can show a preview card
          // without any additional fetch.
          forwardParams.set("repo", repository);
          forwardParams.set("pr", String(prNumber));
          // A Linear review's issue rides along as context for the PR's first
          // session; an existing session above already has its own.
          applyCodesmithExtensionContext(forwardParams, router.query);
          if (response.data.pr) {
            forwardParams.set("pr_preview", JSON.stringify(response.data.pr));
          }
          const path = `/${org}/sessions/new?${forwardParams.toString()}`;
          replaceRoute(
            extensionEmbed ? addCodesmithExtensionEmbed(path) : path,
          );
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err?.response?.data?.message || "Failed to resolve PR session";
        setResolveError(message);
      });

    return () => {
      cancelled = true;
    };
  }, [
    router.isReady,
    router.query,
    codesmithEnabled,
    extensionEmbed,
    codesmithOnlySurface,
    api,
    instanceSlug,
  ]);

  if (codesmithDisabledInEmbed) return <CodesmithDisabledNotice />;

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <KeepAppRailOnMobile />
        <div className="text-center">
          <p className="text-sm text-neutral-500">{error}</p>
        </div>
      </div>
    );
  }

  return <SessionSkeleton />;
}
